import { useState, useMemo, useEffect, useCallback, type ReactNode } from "react";
import {
  Plus,
  Send,
  Flame,
  AlertCircle,
  Info,
  Circle,
  X,
  MessageSquare,
  Clock,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Inbox,
  User,
  ShieldCheck,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import type {
  SupportTicket,
  TicketMessage,
  TicketPriority,
  TicketStatus,
} from "@platform/shared";

// ---------------------------------------------------------------------------
// Types & Config
// ---------------------------------------------------------------------------

interface PriorityConfig {
  readonly icon: ReactNode;
  readonly label: string;
  readonly stripColor: string;
  readonly badgeVariant: "danger" | "warning" | "info" | "secondary";
}

const PRIORITY_CONFIG: Record<TicketPriority, PriorityConfig> = {
  critical: {
    icon: <Flame className="h-3.5 w-3.5" />,
    label: "קריטי",
    stripColor: "bg-red-500",
    badgeVariant: "danger",
  },
  high: {
    icon: <AlertCircle className="h-3.5 w-3.5" />,
    label: "גבוה",
    stripColor: "bg-orange-400",
    badgeVariant: "danger",
  },
  medium: {
    icon: <Info className="h-3.5 w-3.5" />,
    label: "בינוני",
    stripColor: "bg-blue-400",
    badgeVariant: "info",
  },
  low: {
    icon: <Circle className="h-3.5 w-3.5" />,
    label: "נמוך",
    stripColor: "bg-gray-300",
    badgeVariant: "secondary",
  },
};

interface StatusConfig {
  readonly label: string;
  readonly badgeVariant: "info" | "warning" | "success" | "secondary";
}

const STATUS_CONFIG: Record<TicketStatus, StatusConfig> = {
  open: { label: "פתוח", badgeVariant: "info" },
  in_progress: { label: "בטיפול", badgeVariant: "warning" },
  resolved: { label: "נפתר", badgeVariant: "success" },
  closed: { label: "סגור", badgeVariant: "secondary" },
};

type FilterStatus = "all" | TicketStatus;
type FilterPriority = "all" | TicketPriority;

const STATUS_FILTERS: ReadonlyArray<{ value: FilterStatus; label: string }> = [
  { value: "all", label: "הכל" },
  { value: "open", label: "פתוחים" },
  { value: "in_progress", label: "בטיפול" },
  { value: "resolved", label: "נפתרו" },
];

const PRIORITY_FILTERS: ReadonlyArray<{ value: FilterPriority; label: string }> = [
  { value: "all", label: "כל עדיפות" },
  { value: "critical", label: "קריטי" },
  { value: "high", label: "גבוה" },
  { value: "medium", label: "בינוני" },
  { value: "low", label: "נמוך" },
];

interface TenantOption {
  readonly id: string;
  readonly name: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `לפני ${String(mins)} דק'`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `לפני ${String(hours)} שע'`;
  const days = Math.floor(hours / 24);
  return `לפני ${String(days)} ימים`;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("he-IL", {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

// ---------------------------------------------------------------------------
// API helpers
// ---------------------------------------------------------------------------

interface TicketsResponse {
  readonly success: boolean;
  readonly data: SupportTicket[];
  readonly meta: { total: number; limit: number; offset: number };
}

interface TicketDetailResponse {
  readonly success: boolean;
  readonly data: { ticket: SupportTicket; messages: TicketMessage[] };
}

interface TenantsResponse {
  readonly success: boolean;
  readonly data: TenantOption[];
}

// ---------------------------------------------------------------------------
// KPI Stats Bar
// ---------------------------------------------------------------------------

interface StatKpiProps {
  readonly icon: ReactNode;
  readonly label: string;
  readonly value: string | number;
  readonly accent?: string | undefined;
}

function StatKpi({ icon, label, value, accent }: StatKpiProps): ReactNode {
  return (
    <Card className="relative overflow-hidden border-0 shadow-sm bg-gradient-to-br from-card to-muted/30 flex-1 min-w-[120px]">
      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent pointer-events-none" />
      <CardContent className="p-4 flex items-center gap-3 relative">
        <div
          className="flex h-9 w-9 items-center justify-center rounded-lg flex-shrink-0"
          style={{ background: accent ? `${accent}20` : undefined }}
        >
          {icon}
        </div>
        <div className="min-w-0">
          <p className="text-xs text-muted-foreground truncate">{label}</p>
          <p className="text-2xl font-bold tabular-nums leading-tight">{value}</p>
        </div>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Message Thread (inline accordion)
// ---------------------------------------------------------------------------

interface MessageThreadProps {
  readonly ticket: SupportTicket;
  readonly onClose: () => void;
  readonly onStatusChange: (ticketId: string, status: TicketStatus) => void;
}

function MessageThread({ ticket, onClose, onStatusChange }: MessageThreadProps): ReactNode {
  const [messages, setMessages] = useState<ReadonlyArray<TicketMessage>>([]);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    fetch(`/super/api/support/tickets/${ticket.id}`, { credentials: "include" })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json() as Promise<TicketDetailResponse>;
      })
      .then((json) => setMessages(json.data.messages))
      .catch(() => {});
  }, [ticket.id]);

  const handleSend = async () => {
    const trimmed = reply.trim();
    if (!trimmed || sending) return;
    setSending(true);
    try {
      const res = await fetch(`/super/api/support/tickets/${ticket.id}/messages`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ sender: "admin", message: trimmed }),
      });
      const json = (await res.json()) as { success: boolean; error?: string };
      if (!json.success) throw new Error(json.error ?? "שגיאה בשליחה");
      const newMsg: TicketMessage = {
        id: `local-${String(Date.now())}`,
        ticketId: ticket.id,
        sender: "admin",
        message: trimmed,
        createdAt: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, newMsg]);
      setReply("");

      // Auto-move to in_progress if still open
      if (ticket.status === "open") {
        onStatusChange(ticket.id, "in_progress");
      }
    } catch {
      // keep reply on failure
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      void handleSend();
    }
  };

  return (
    <div className="border-t border-border/60 bg-muted/20 rounded-b-xl overflow-hidden">
      {/* Thread messages */}
      <div className="px-4 pt-4 pb-2 space-y-3 max-h-64 overflow-y-auto">
        {messages.length === 0 && (
          <p className="text-center text-xs text-muted-foreground py-4">אין הודעות עדיין</p>
        )}
        {messages.map((msg) => {
          const isAdmin = msg.sender === "admin";
          return (
            <div key={msg.id} className={`flex gap-2 ${isAdmin ? "flex-row" : "flex-row-reverse"}`}>
              <div
                className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-xs font-semibold ${
                  isAdmin
                    ? "bg-primary/15 text-primary"
                    : "bg-muted-foreground/20 text-muted-foreground"
                }`}
              >
                {isAdmin ? <ShieldCheck className="h-3.5 w-3.5" /> : <User className="h-3.5 w-3.5" />}
              </div>
              <div
                className={`max-w-[80%] rounded-xl px-3 py-2 text-sm ${
                  isAdmin
                    ? "bg-primary/10 text-foreground"
                    : "bg-card text-foreground border border-border/60"
                }`}
              >
                <p className="text-[10px] font-medium mb-0.5 text-muted-foreground">
                  {isAdmin ? "מנהל" : (ticket.tenantName ?? ticket.tenantId)} · {formatDate(msg.createdAt)}
                </p>
                <p className="whitespace-pre-wrap leading-snug">{msg.message}</p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Reply box */}
      <div className="px-4 pb-4 pt-2 border-t border-border/40">
        <div className="flex gap-2 items-end">
          <textarea
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="כתוב תגובה... (Enter לשליחה)"
            rows={2}
            className="flex-1 min-h-[44px] max-h-28 rounded-lg border border-input bg-background px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-ring"
          />
          <button
            type="button"
            onClick={() => void handleSend()}
            disabled={!reply.trim() || sending}
            className="h-11 w-11 flex-shrink-0 flex items-center justify-center rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <Send className="h-4 w-4" />
          </button>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="mt-2 w-full text-xs text-muted-foreground hover:text-foreground transition-colors py-1"
        >
          סגור שיחה
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Ticket Card
// ---------------------------------------------------------------------------

interface TicketCardProps {
  readonly ticket: SupportTicket;
  readonly isExpanded: boolean;
  readonly onToggle: () => void;
  readonly onStatusChange: (ticketId: string, status: TicketStatus) => void;
}

function TicketCard({ ticket, isExpanded, onToggle, onStatusChange }: TicketCardProps): ReactNode {
  const pri = PRIORITY_CONFIG[ticket.priority];
  const sts = STATUS_CONFIG[ticket.status];

  return (
    <div
      className={`rounded-xl border bg-card shadow-sm overflow-hidden transition-all duration-200 ${
        isExpanded ? "ring-1 ring-primary/30" : "hover:shadow-md"
      }`}
    >
      {/* Card body */}
      <button
        type="button"
        onClick={onToggle}
        className="w-full text-start flex items-stretch min-h-[72px]"
      >
        {/* Priority strip */}
        <div className={`w-1.5 flex-shrink-0 ${pri.stripColor} rounded-s-xl`} />

        {/* Content */}
        <div className="flex flex-1 items-center gap-3 px-4 py-3 min-w-0">
          {/* Priority icon */}
          <div
            className={`hidden sm:flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg ${
              ticket.priority === "critical"
                ? "bg-red-100 text-red-600 dark:bg-red-500/15 dark:text-red-400"
                : ticket.priority === "high"
                ? "bg-orange-100 text-orange-600 dark:bg-orange-500/15 dark:text-orange-400"
                : ticket.priority === "medium"
                ? "bg-blue-100 text-blue-600 dark:bg-blue-500/15 dark:text-blue-400"
                : "bg-muted text-muted-foreground"
            }`}
          >
            {pri.icon}
          </div>

          {/* Main info */}
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
              <span className="text-sm font-semibold truncate">{ticket.subject}</span>
              <Badge variant={sts.badgeVariant} className="flex-shrink-0">{sts.label}</Badge>
            </div>
            <div className="flex items-center gap-2 mt-0.5">
              <span className="text-xs text-muted-foreground">{ticket.tenantName ?? ticket.tenantId}</span>
              <span className="text-muted-foreground/40">·</span>
              <span className="text-xs text-muted-foreground flex items-center gap-0.5">
                <Clock className="h-3 w-3" />
                {timeAgo(ticket.createdAt)}
              </span>
            </div>
          </div>

          {/* Expand icon */}
          <div className="flex-shrink-0 text-muted-foreground">
            {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </div>
        </div>
      </button>

      {/* Expanded thread */}
      {isExpanded && (
        <MessageThread
          ticket={ticket}
          onClose={onToggle}
          onStatusChange={onStatusChange}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// New Ticket Inline Form
// ---------------------------------------------------------------------------

interface NewTicketFormProps {
  readonly tenants: ReadonlyArray<TenantOption>;
  readonly onClose: () => void;
  readonly onCreated: () => void;
}

function NewTicketForm({ tenants, onClose, onCreated }: NewTicketFormProps): ReactNode {
  const [subject, setSubject] = useState("");
  const [selectedTenantId, setSelectedTenantId] = useState("");
  const [priority, setPriority] = useState<TicketPriority>("medium");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedTenantId || !subject.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch("/super/api/support/tickets", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          tenantId: selectedTenantId,
          subject: subject.trim(),
          description: message.trim() || null,
          priority,
        }),
      });
      const json = (await res.json()) as { success: boolean; error?: string };
      if (!json.success) throw new Error(json.error ?? "שגיאה ביצירת פנייה");
      onCreated();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "שגיאה לא ידועה");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card className="border-primary/20 shadow-md ring-1 ring-primary/20">
      <div className="p-5" dir="rtl">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-bold">פנייה חדשה</h3>
          <button
            type="button"
            onClick={onClose}
            className="h-8 w-8 flex items-center justify-center rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4">
          {/* Tenant selector — button chips */}
          <div className="space-y-2">
            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              דייר
            </label>
            <div className="flex flex-wrap gap-2">
              {tenants.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setSelectedTenantId(t.id === selectedTenantId ? "" : t.id)}
                  className={`min-h-[36px] rounded-full px-3 py-1 text-xs font-medium border transition-colors ${
                    selectedTenantId === t.id
                      ? "bg-primary text-primary-foreground border-primary"
                      : "bg-background text-foreground border-border hover:border-primary/50 hover:bg-primary/5"
                  }`}
                >
                  {t.name}
                </button>
              ))}
            </div>
          </div>

          {/* Subject */}
          <div className="space-y-2">
            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              נושא
            </label>
            <Input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="תיאור קצר של הפנייה"
              className="min-h-[44px]"
              required
              maxLength={200}
            />
          </div>

          {/* Priority — button group */}
          <div className="space-y-2">
            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              עדיפות
            </label>
            <div className="flex gap-2 flex-wrap">
              {(["low", "medium", "high", "critical"] as const).map((p) => {
                const cfg = PRIORITY_CONFIG[p];
                return (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setPriority(p)}
                    className={`min-h-[36px] inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium border transition-colors ${
                      priority === p
                        ? "bg-primary text-primary-foreground border-primary"
                        : "bg-background text-foreground border-border hover:border-primary/50"
                    }`}
                  >
                    {cfg.icon}
                    {cfg.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Message */}
          <div className="space-y-2">
            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              הודעה
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="פרט את הבעיה..."
              rows={3}
              className="w-full min-h-[80px] rounded-lg border border-input bg-background px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>

          {error && (
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
          )}

          <div className="flex gap-2 pt-1">
            <Button
              type="submit"
              className="flex-1 min-h-[44px]"
              disabled={submitting || !selectedTenantId || !subject.trim()}
            >
              <Plus className="h-4 w-4" />
              {submitting ? "שולח..." : "צור פנייה"}
            </Button>
            <Button type="button" variant="outline" onClick={onClose} className="min-h-[44px] px-4">
              ביטול
            </Button>
          </div>
        </form>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Main Page
// ---------------------------------------------------------------------------

export function SuperSupportPage(): ReactNode {
  const [statusFilter, setStatusFilter] = useState<FilterStatus>("all");
  const [priorityFilter, setPriorityFilter] = useState<FilterPriority>("all");
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [showNewTicket, setShowNewTicket] = useState(false);

  const [tickets, setTickets] = useState<ReadonlyArray<SupportTicket>>([]);
  const [loading, setLoading] = useState(true);
  const [tenants, setTenants] = useState<ReadonlyArray<TenantOption>>([]);

  const fetchTickets = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({ limit: "50", offset: "0" });
    if (statusFilter !== "all") params.set("status", statusFilter);
    if (priorityFilter !== "all") params.set("priority", priorityFilter);

    fetch(`/super/api/support/tickets?${params.toString()}`, { credentials: "include" })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json() as Promise<TicketsResponse>;
      })
      .then((json) => setTickets(json.data))
      .catch(() => setTickets([]))
      .finally(() => setLoading(false));
  }, [statusFilter, priorityFilter]);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  useEffect(() => {
    fetch("/super/api/tenants", { credentials: "include" })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json() as Promise<TenantsResponse>;
      })
      .then((json) => setTenants(json.data.map((t) => ({ id: t.id, name: t.name }))))
      .catch(() => {});
  }, []);

  const handleStatusChange = useCallback(async (ticketId: string, status: TicketStatus) => {
    try {
      const res = await fetch(`/super/api/support/tickets/${ticketId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ status }),
      });
      const json = (await res.json()) as { success: boolean };
      if (json.success) {
        setTickets((prev) =>
          prev.map((t) => (t.id === ticketId ? { ...t, status } : t))
        );
      }
    } catch {
      // silent — UI state unchanged
    }
  }, []);

  const openCount = tickets.filter((t) => t.status === "open").length;
  const inProgressCount = tickets.filter((t) => t.status === "in_progress").length;

  const filtered = useMemo(() => tickets, [tickets]);

  const handleToggle = (id: string) => {
    setExpandedId((prev) => (prev === id ? null : id));
  };

  const statusFilterLabel: Record<FilterStatus, string> = {
    all: "הכל",
    open: "פתוחים",
    in_progress: "בטיפול",
    resolved: "נפתרו",
    closed: "סגורים",
  };

  return (
    <div className="space-y-5 p-4 md:p-6" dir="rtl">
      {/* Page header */}
      <div className="flex items-center justify-between gap-4">
        <h1 className="text-2xl font-bold tracking-tight">תמיכה</h1>
        <Button
          onClick={() => {
            setShowNewTicket((v) => !v);
          }}
          className="min-h-[44px]"
        >
          <Plus className="h-4 w-4" />
          פנייה חדשה
        </Button>
      </div>

      {/* KPI Stats bar */}
      <div className="flex gap-3 flex-wrap">
        <StatKpi
          icon={<Inbox className="h-4 w-4 text-blue-500" />}
          label="פתיחות"
          value={openCount}
          accent="#3b82f6"
        />
        <StatKpi
          icon={<Clock className="h-4 w-4 text-yellow-500" />}
          label="בטיפול"
          value={inProgressCount}
          accent="#eab308"
        />
        <StatKpi
          icon={<CheckCircle2 className="h-4 w-4 text-green-500" />}
          label="סה״כ פניות"
          value={tickets.length}
          accent="#22c55e"
        />
      </div>

      {/* New Ticket Form (inline) */}
      {showNewTicket && (
        <NewTicketForm
          tenants={tenants}
          onClose={() => setShowNewTicket(false)}
          onCreated={fetchTickets}
        />
      )}

      {/* Filters */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        {/* Status filter */}
        <div className="flex gap-1 rounded-lg border p-1 bg-muted/30 w-fit">
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.value}
              type="button"
              onClick={() => setStatusFilter(f.value)}
              className={`min-h-[36px] rounded-md px-3 py-1 text-sm font-medium transition-colors ${
                statusFilter === f.value
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Priority filter */}
        <div className="flex gap-1.5 flex-wrap">
          {PRIORITY_FILTERS.map((f) => (
            <button
              key={f.value}
              type="button"
              onClick={() => setPriorityFilter(f.value)}
              className={`min-h-[36px] rounded-full px-3 py-1 text-xs font-medium border transition-colors ${
                priorityFilter === f.value
                  ? "bg-primary text-primary-foreground border-primary"
                  : "bg-background text-foreground border-border hover:border-primary/40"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Ticket list */}
      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-[72px] rounded-xl bg-muted/40 animate-pulse" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center gap-3 py-14 text-center">
            <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center">
              <Inbox className="h-6 w-6 text-muted-foreground" />
            </div>
            <p className="font-medium text-muted-foreground">
              אין פניות {statusFilter !== "all" ? statusFilterLabel[statusFilter] : ""}
            </p>
            <p className="text-xs text-muted-foreground/60">
              {priorityFilter !== "all" ? `עדיפות: ${PRIORITY_CONFIG[priorityFilter as TicketPriority]?.label ?? ""}` : ""}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filtered.map((ticket) => (
            <TicketCard
              key={ticket.id}
              ticket={ticket}
              isExpanded={expandedId === ticket.id}
              onToggle={() => handleToggle(ticket.id)}
              onStatusChange={(id, status) => void handleStatusChange(id, status)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
